import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Eu, Falha } from '../../acesso';
import type { Sessao } from '../../auth';
import { lerFila, lerResponsaveis, numeroDoCaso, urlDoRelatorio } from '../../auditoria';
import type {
  CasoDaFila,
  EtiquetaDoCaso,
  FilaDoAuditor,
  FiltrosDaFila,
  LocalDaConferencia,
  OrdemDaFila,
  Responsaveis,
} from '../../auditoria';
import { ErroDeArea, EstadoVazio, SemAcesso } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { FotoDoProduto } from '../auditoria/FotoDoProduto';
import {
  Laboratorio,
  ROTULO_DO_LOCAL,
  Selo,
  comSinal,
  guardarCentral,
  guardarLista,
  papeisDe,
  quandoBR,
  seloDe,
  ultimaCentral,
} from './comum';
import { NumerosEmLinha } from './TresNumeros';
import './divergencias.css';

/**
 * CENTRAL DE DIVERGÊNCIAS 3.0 PREMIUM (dono, 13/10/2026): "confira, investigue e resolva".
 *
 *   Minha fila   — quem confere vê SÓ o seu local (Igor: prateleira · Felipe: estoque), pela lista
 *                  de responsáveis; quem só decide vê o que espera decisão.
 *   Todas        — os casos abertos.  Aguardando decisão — as duas contagens feitas.
 *   Concluídas / Histórico — com período.
 *
 * Os números do topo vêm do servidor, no MESMO recorte (setor, balconista, período) e contam casos
 * distintos. Filtros moram no endereço (voltar devolve o mesmo recorte). Cada card mostra só o
 * essencial; o detalhe técnico fica dentro do caso.
 */

type Aba = 'prateleira' | 'estoque' | 'ajustar' | 'todas' | 'concluidas' | 'historico';
type Periodo = 'tudo' | 'hoje' | 'ontem' | '7' | '30' | 'periodo';
type Status = '' | 'prateleira' | 'estoque' | 'decisao' | 'atrasada' | 'reincidente';
type Faixa = '' | '1' | '2-3' | '4-5' | '6-10' | '10+';

interface Filtros {
  /** null = a aba de quem abre (pela escala e pelo papel). */
  readonly aba: Aba | null;
  readonly busca: string;
  readonly periodo: Periodo;
  readonly de: string;
  readonly ate: string;
  readonly status: Status;
  readonly ordem: OrdemDaFila;
  readonly setor: string;
  /** Grupo do cadastro do HOS (GENERICOS, SIMILARES…). */
  readonly grupo: string;
  readonly pessoa: string;
  readonly faixa: Faixa;
  readonly reincidentes: boolean;
  readonly pagina: number;
}

const POR_PAGINA = 30;
/**
 * As TRÊS PARTES (dono/Carlos, 14/10): Prateleira (Igor) · Estoque (Felipe) · Ajustar (decisão).
 * "Ajustar" não quer dizer mexer no saldo do HOS: é a fila de investigar e decidir.
 */
const ABAS: readonly { aba: Aba; rotulo: string }[] = [
  { aba: 'prateleira', rotulo: 'Prateleira' },
  { aba: 'estoque', rotulo: 'Estoque' },
  { aba: 'ajustar', rotulo: 'Ajustar' },
  { aba: 'todas', rotulo: 'Todas' },
  { aba: 'concluidas', rotulo: 'Concluídas' },
  { aba: 'historico', rotulo: 'Histórico' },
];
const PERIODOS: readonly { v: Periodo; r: string }[] = [
  { v: 'tudo', r: 'Todo o período' },
  { v: 'hoje', r: 'Hoje' },
  { v: 'ontem', r: 'Ontem' },
  { v: '7', r: 'Últimos 7 dias' },
  { v: '30', r: 'Últimos 30 dias' },
  { v: 'periodo', r: 'Escolher datas…' },
];
const STATUS: readonly { v: Status; r: string }[] = [
  { v: '', r: 'Todos os status' },
  { v: 'prateleira', r: 'Aguardando prateleira' },
  { v: 'estoque', r: 'Aguardando estoque' },
  { v: 'decisao', r: 'Pronto para decisão' },
  { v: 'atrasada', r: 'Prazo vencido' },
  { v: 'reincidente', r: 'Reincidente' },
];
const ORDENS: readonly { v: OrdemDaFila; r: string }[] = [
  { v: 'PRIORIDADE', r: 'Prioridade' },
  { v: 'ROTA', r: 'Rota na loja' },
  { v: 'DIFERENCA', r: 'Maior diferença' },
  { v: 'ANTIGA', r: 'Mais antiga' },
  { v: 'CATEGORIA', r: 'Categoria' },
  { v: 'AZ', r: 'Produto A–Z' },
];
const FAIXAS: Readonly<Record<Exclude<Faixa, ''>, [number, number | null]>> = {
  '1': [1, 1],
  '2-3': [2, 3],
  '4-5': [4, 5],
  '6-10': [6, 10],
  '10+': [11, null],
};

const hojeBR = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
function maisDias(dia: string, n: number) {
  const d = new Date(`${dia}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function intervalo(f: Filtros): { de: string; ate: string } | null {
  const h = hojeBR();
  switch (f.periodo) {
    case 'hoje':
      return { de: h, ate: h };
    case 'ontem':
      return { de: maisDias(h, -1), ate: maisDias(h, -1) };
    case '7':
      return { de: maisDias(h, -6), ate: h };
    case '30':
      return { de: maisDias(h, -29), ate: h };
    case 'periodo': {
      const de = /^\d{4}-\d{2}-\d{2}$/.test(f.de) ? f.de : h;
      const ate = /^\d{4}-\d{2}-\d{2}$/.test(f.ate) && f.ate >= de ? f.ate : de;
      return { de, ate };
    }
    default:
      return null;
  }
}

function deParametros(p: URLSearchParams): Filtros {
  const aba = p.get('aba');
  const per = p.get('per');
  const st = p.get('status');
  const ordem = p.get('ordem');
  const local = p.get('local');
  const faixa = p.get('dif');
  // Endereços antigos: "minha fila" + local e "aguardando decisão".
  const antiga: Aba | null =
    aba === 'minha'
      ? local === 'ESTOQUE'
        ? 'estoque'
        : local === 'PRATELEIRA'
          ? 'prateleira'
          : null
      : aba === 'decisao'
        ? 'ajustar'
        : null;
  return {
    aba: ABAS.some((a) => a.aba === aba) ? (aba as Aba) : antiga,
    busca: p.get('busca') ?? '',
    periodo: PERIODOS.some((x) => x.v === per) ? (per as Periodo) : 'tudo',
    de: p.get('de') ?? '',
    ate: p.get('ate') ?? '',
    status: STATUS.some((x) => x.v === st) ? (st as Status) : '',
    ordem: ORDENS.some((x) => x.v === ordem) ? (ordem as OrdemDaFila) : 'PRIORIDADE',
    setor: p.get('setor') ?? '',
    grupo: p.get('grupo') ?? '',
    pessoa: p.get('pessoa') ?? '',
    faixa: faixa && faixa in FAIXAS ? (faixa as Faixa) : '',
    reincidentes: p.get('reinc') === '1',
    pagina: Math.max(1, Number(p.get('pag')) || 1),
  };
}
function endereco(f: Filtros): string {
  const p = new URLSearchParams();
  if (f.aba) p.set('aba', f.aba);
  if (f.busca.trim()) p.set('busca', f.busca.trim());
  if (f.periodo !== 'tudo') p.set('per', f.periodo);
  if (f.periodo === 'periodo') {
    if (f.de) p.set('de', f.de);
    if (f.ate) p.set('ate', f.ate);
  }
  if (f.status) p.set('status', f.status);
  if (f.ordem !== 'PRIORIDADE') p.set('ordem', f.ordem);
  if (f.setor) p.set('setor', f.setor);
  if (f.grupo) p.set('grupo', f.grupo);
  if (f.pessoa) p.set('pessoa', f.pessoa);
  if (f.faixa) p.set('dif', f.faixa);
  if (f.reincidentes) p.set('reinc', '1');
  if (f.pagina > 1) p.set('pag', String(f.pagina));
  const q = p.toString();
  return `#/divergencias${q ? `?${q}` : ''}`;
}

/** A aba de quem abre sem escolher: pela lista de responsáveis; sem lista, pelo papel. */
function abaDaPessoa(papeis: ReturnType<typeof papeisDe>, resp: Responsaveis | null): Aba {
  if (resp?.minhaFuncao.prateleira && papeis.prateleira) return 'prateleira';
  if (resp?.minhaFuncao.estoque && papeis.estoque) return 'estoque';
  // Quem decide e não está escalado para contar começa pelo que espera decisão.
  if (papeis.decide) return 'ajustar';
  if (papeis.prateleira) return 'prateleira';
  if (papeis.estoque) return 'estoque';
  return 'todas';
}

/** O local de conferência de uma aba (Prateleira/Estoque); as outras não conferem. */
const localDaAba = (a: Aba): LocalDaConferencia | null =>
  a === 'prateleira' ? 'PRATELEIRA' : a === 'estoque' ? 'ESTOQUE' : null;

function consulta(f: Filtros, aba: Aba): FiltrosDaFila {
  const per = intervalo(f);
  const etiquetas: EtiquetaDoCaso[] = [];
  if (f.reincidentes || f.status === 'reincidente') etiquetas.push('REINCIDENTE');
  if (f.status === 'atrasada') etiquetas.push('ATRASADA');
  const lim = f.faixa ? FAIXAS[f.faixa] : null;
  const base: FiltrosDaFila = {
    busca: f.busca.trim() || null,
    setor: f.setor || null,
    grupo: f.grupo || null,
    pessoa: f.pessoa || null,
    ordem: f.ordem,
    etiquetas,
    difMin: lim ? lim[0] : null,
    difMax: lim ? lim[1] : null,
    limite: POR_PAGINA,
    pagina: f.pagina,
    ...(per ? { de: per.de, ate: per.ate } : {}),
  };
  const etapaDoStatus =
    f.status === 'prateleira'
      ? ('PRATELEIRA' as const)
      : f.status === 'estoque'
        ? ('ESTOQUE' as const)
        : f.status === 'decisao'
          ? ('AJUSTE' as const)
          : null;
  switch (aba) {
    case 'prateleira':
      return { ...base, conferir: 'PRATELEIRA' };
    case 'estoque':
      return { ...base, conferir: 'ESTOQUE' };
    case 'ajustar':
      return { ...base, etapa: 'AJUSTE' };
    case 'todas':
      return { ...base, somenteAbertos: true, etapa: etapaDoStatus };
    case 'concluidas':
      return { ...base, aba: 'concluidas', ...(per ? {} : { dias: 366 }) };
    case 'historico':
      return { ...base, aba: 'historico', etapa: etapaDoStatus, ...(per ? {} : { dias: 366 }) };
  }
}

type Estado =
  | { readonly fase: 'carregando' }
  | { readonly fase: 'pronto'; readonly dados: FilaDoAuditor }
  | { readonly fase: 'erro'; readonly falha: Falha; readonly ultimo: FilaDoAuditor | null };

/** "GENERICOS" → "Genéricos": só a escrita; o valor do filtro é o do cadastro. */
const GRAFIA: Readonly<Record<string, string>> = {
  GENERICOS: 'Genéricos',
  SIMILARES: 'Similares',
  PERFUMARIA: 'Perfumaria',
  MEDICAMENTOS: 'Medicamentos',
  ALIMENTOS: 'Alimentos',
  ETICOS: 'Éticos',
};
const nomeDoGrupo = (g: string) =>
  GRAFIA[g] ?? g.charAt(0).toUpperCase() + g.slice(1).toLowerCase();

export function CentralPremium({
  sessao,
  eu,
  parametros,
}: {
  sessao: Sessao;
  eu: Eu | null;
  parametros: URLSearchParams;
}) {
  void sessao;
  const papeis = papeisDe(eu);
  const podeVer =
    papeis.prateleira ||
    papeis.estoque ||
    papeis.decide ||
    papeis.gestao ||
    (eu?.capacidades.includes('giro.auditar') ?? false);

  const [f, setF] = useState<Filtros>(() => {
    // Sem filtro no endereço: volta o último recorte desta aba do navegador.
    if (!parametros.toString()) {
      const ult = ultimaCentral();
      const i = ult.indexOf('?');
      if (i > 0) return deParametros(new URLSearchParams(ult.slice(i + 1)));
    }
    return deParametros(parametros);
  });
  const [buscaDigitada, setBuscaDigitada] = useState(f.busca);
  const [mais, setMais] = useState(() =>
    Boolean(f.setor || f.faixa || f.reincidentes || f.status || f.ordem !== 'PRIORIDADE'),
  );
  const [resp, setResp] = useState<Responsaveis | null>(null);
  const [estado, setEstado] = useState<Estado>({ fase: 'carregando' });
  const [atualizadoEm, setAtualizadoEm] = useState<string | null>(null);
  const pedido = useRef(0);

  useEffect(() => {
    void lerResponsaveis().then((r) => {
      if (r.ok) setResp(r.dados);
    });
  }, []);

  // Aba que a pessoa não usa não aparece (a gestão vê as três partes).
  const abas = ABAS.filter((a) =>
    a.aba === 'prateleira'
      ? papeis.prateleira || papeis.decide || papeis.gestao
      : a.aba === 'estoque'
        ? papeis.estoque || papeis.decide || papeis.gestao
        : a.aba === 'ajustar'
          ? papeis.decide || papeis.gestao
          : true,
  );
  const pedida = f.aba ?? abaDaPessoa(papeis, resp);
  const aba: Aba = abas.some((a) => a.aba === pedida) ? pedida : (abas[0]?.aba ?? 'todas');
  const local = localDaAba(aba);
  const filtros = useMemo(() => ({ ...f, aba }), [f, aba]);

  // O endereço espelha os filtros (sem recarregar a tela) e fica guardado para o "voltar".
  useEffect(() => {
    const e = endereco(filtros);
    if (window.location.hash !== e) window.history.replaceState(null, '', e);
    guardarCentral(e);
  }, [filtros]);

  // Busca com espera: só consulta quando a pessoa para de digitar.
  useEffect(() => {
    if (buscaDigitada === f.busca) return;
    const t = window.setTimeout(
      () => setF((x) => ({ ...x, busca: buscaDigitada, pagina: 1 })),
      350,
    );
    return () => window.clearTimeout(t);
  }, [buscaDigitada, f.busca]);

  const carregar = useCallback(
    async (silencioso: boolean) => {
      // Só a resposta do ÚLTIMO pedido vale: filtro trocado no meio não mistura listas.
      const n = ++pedido.current;
      if (!silencioso) setEstado((e) => (e.fase === 'pronto' ? e : { fase: 'carregando' }));
      const r = await lerFila(consulta(filtros, aba));
      if (n !== pedido.current) return;
      if (r.ok) {
        setEstado({ fase: 'pronto', dados: r.dados });
        setAtualizadoEm(r.dados.atualizadoEm);
        guardarLista(r.dados.casos.map((c) => c.id));
      } else
        setEstado((e) => ({
          fase: 'erro',
          falha: r.falha,
          ultimo: e.fase === 'pronto' ? e.dados : e.fase === 'erro' ? e.ultimo : null,
        }));
    },
    [filtros, aba],
  );

  useEffect(() => {
    if (podeVer) void carregar(false);
  }, [carregar, podeVer]);
  // A fila se atualiza sozinha, sem recarregar a tela, só quando está visível.
  useEffect(() => {
    if (!podeVer) return;
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') void carregar(true);
    }, 30_000);
    return () => window.clearInterval(t);
  }, [carregar, podeVer]);

  if (!podeVer) return <SemAcesso area="As divergências" />;

  const dados =
    estado.fase === 'pronto' ? estado.dados : estado.fase === 'erro' ? estado.ultimo : null;
  const ind = dados?.indicadores;
  const mudar = (x: Partial<Filtros>) => setF((y) => ({ ...y, pagina: 1, ...x }));
  const aqui = endereco(filtros);
  // Os filtros viajam para a conferência: o "próximo" respeita grupo, balconista e período.
  const paraConferir = (l: LocalDaConferencia, inicio?: string) => {
    const q = new URLSearchParams({ local: l });
    if (inicio) q.set('inicio', inicio);
    const c = consulta(filtros, aba);
    if (c.setor) q.set('setor', c.setor);
    if (c.grupo) q.set('grupo', c.grupo);
    if (c.pessoa) q.set('pessoa', c.pessoa);
    if (c.busca) q.set('busca', c.busca);
    if (c.de) q.set('de', c.de);
    if (c.ate) q.set('ate', c.ate);
    if (filtros.ordem !== 'PRIORIDADE') q.set('ordem', filtros.ordem);
    q.set('volta', aqui);
    return `#/conferir?${q.toString()}`;
  };
  const paraCaso = (id: string) => `#/caso?id=${id}&volta=${encodeURIComponent(aqui)}`;

  const kpis: readonly {
    chave: string;
    rotulo: string;
    icone: NomeDoIcone;
    valor: number | undefined;
    tom?: 'ok' | 'alerta';
    ativo: boolean;
    ir: () => void;
  }[] = [
    {
      chave: 'conf',
      rotulo: 'Aguardando conferência',
      icone: 'clock',
      valor: ind?.aguardandoConferencia ?? ind?.aguardandoPrateleira,
      ativo: aba === 'todas' && !f.status,
      ir: () => mudar({ aba: 'todas', status: '' }),
    },
    {
      chave: 'prat',
      rotulo: 'Prateleira pendente',
      icone: 'shelf',
      valor: ind?.aguardandoPrateleira,
      ativo: aba === 'prateleira',
      ir: () => mudar({ aba: 'prateleira', status: '' }),
    },
    {
      chave: 'est',
      rotulo: 'Estoque pendente',
      icone: 'box',
      valor: ind?.aguardandoEstoque,
      ativo: aba === 'estoque',
      ir: () => mudar({ aba: 'estoque', status: '' }),
    },
    {
      chave: 'dec',
      rotulo: 'Prontos para decisão',
      icone: 'gauge',
      valor: ind?.aguardandoAjuste,
      tom: 'alerta',
      ativo: aba === 'ajustar',
      ir: () => mudar({ aba: 'ajustar', status: '' }),
    },
    {
      chave: 'hoje',
      rotulo: 'Concluídos hoje',
      icone: 'check',
      valor: ind?.concluidasHoje,
      tom: 'ok',
      ativo: aba === 'concluidas' && f.periodo === 'hoje',
      ir: () => mudar({ aba: 'concluidas', periodo: 'hoje' }),
    },
  ];
  const contagemDaAba = (a: Aba): number | null =>
    !ind
      ? null
      : a === 'prateleira'
        ? ind.aguardandoPrateleira
        : a === 'estoque'
          ? ind.aguardandoEstoque
          : a === 'ajustar'
            ? ind.aguardandoAjuste
            : null;
  const mostraStatus = aba === 'todas' || aba === 'historico';
  const conferindo = local !== null;
  const algumFiltro = Boolean(
    f.grupo || f.pessoa || f.setor || f.faixa || f.reincidentes || f.status,
  );
  const confereAqui =
    local === 'PRATELEIRA' ? papeis.prateleira : local === 'ESTOQUE' ? papeis.estoque : false;

  return (
    <section className="dp">
      <header className="dp-topo">
        <div>
          <h1 className="dp-titulo">Divergências</h1>
          <p className="dp-sub">Confira, investigue e resolva diferenças de estoque.</p>
        </div>
        <div className="dp-acoes">
          {local && confereAqui && (
            <a className="dp-btn dp-btn-primario" href={paraConferir(local)}>
              <Icone nome="scan" tamanho={16} />
              Começar conferência · {ROTULO_DO_LOCAL[local]}
            </a>
          )}
          {papeis.gestao && (
            <a className="dp-btn" href="#/qualidade-equipe">
              <Icone nome="users" tamanho={16} />
              Qualidade da Equipe
            </a>
          )}
          {papeis.gestao && (
            <a className="dp-btn" href="#/divergencias-painel">
              <Icone nome="gauge" tamanho={16} />
              Indicadores
            </a>
          )}
          {papeis.gestao && (
            <a className="dp-btn" href={urlDoRelatorio({ ...consulta(filtros, aba), limite: 500 })}>
              <Icone nome="down" tamanho={16} />
              Exportar
            </a>
          )}
        </div>
      </header>

      <div className="dp-kpis">
        {kpis.map((k) => (
          <button
            key={k.chave}
            type="button"
            className={`dp-kpi${k.tom === 'ok' ? ' dp-kpi-ok' : k.tom === 'alerta' && (k.valor ?? 0) > 0 ? ' dp-kpi-alerta' : ''}`}
            aria-pressed={k.ativo}
            onClick={k.ir}
          >
            <span className="dp-kpi-rotulo">
              <Icone nome={k.icone} tamanho={14} />
              {k.rotulo}
            </span>
            <span className="dp-kpi-valor">{k.valor === undefined ? '—' : k.valor}</span>
          </button>
        ))}
      </div>

      <div className="dp-abas" role="tablist" aria-label="Filas de divergências">
        {abas.map((a) => {
          const n = contagemDaAba(a.aba);
          return (
            <button
              key={a.aba}
              type="button"
              role="tab"
              className="dp-aba"
              aria-selected={aba === a.aba}
              onClick={() => mudar({ aba: a.aba, status: '' })}
            >
              {a.rotulo}
              {n !== null && <span className="dp-aba-n">{n}</span>}
            </button>
          );
        })}
      </div>

      <div className="dp-filtros">
        <label className="dp-busca">
          <Icone nome="search" tamanho={16} />
          <input
            type="search"
            value={buscaDigitada}
            onChange={(e) => setBuscaDigitada(e.target.value)}
            placeholder="Produto, código HOS, EAN, balconista ou nº do caso"
            aria-label="Buscar"
          />
        </label>
        <select
          className="dp-select"
          value={f.grupo}
          onChange={(e) => mudar({ grupo: e.target.value })}
          aria-label="Grupo"
        >
          <option value="">Todos os grupos</option>
          {(dados?.grupos ?? []).map((g) => (
            <option key={g} value={g}>
              {nomeDoGrupo(g)}
            </option>
          ))}
        </select>
        <select
          className="dp-select"
          value={f.pessoa}
          onChange={(e) => mudar({ pessoa: e.target.value })}
          aria-label="Balconista"
        >
          <option value="">Todos os balconistas</option>
          {(dados?.pessoas ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.nome}
            </option>
          ))}
        </select>
        <select
          className="dp-select"
          value={f.periodo}
          onChange={(e) => mudar({ periodo: e.target.value as Periodo })}
          aria-label="Período (data da ocorrência)"
          title="Pela data da ocorrência (o dia em que a divergência nasceu)"
        >
          {PERIODOS.map((p) => (
            <option key={p.v} value={p.v}>
              {p.r}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="dp-btn"
          aria-expanded={mais}
          onClick={() => setMais((m) => !m)}
        >
          <Icone nome="filter" tamanho={16} />
          Mais filtros
        </button>
      </div>

      {f.periodo === 'periodo' && (
        <div className="dp-filtros">
          <label className="dp-rotulo">
            De
            <input
              className="dp-campo"
              type="date"
              value={f.de}
              onChange={(e) => mudar({ de: e.target.value })}
            />
          </label>
          <label className="dp-rotulo">
            Até
            <input
              className="dp-campo"
              type="date"
              value={f.ate}
              onChange={(e) => mudar({ ate: e.target.value })}
            />
          </label>
        </div>
      )}
      {f.periodo !== 'tudo' && (
        <p className="dp-dica" style={{ margin: 0 }}>
          Período pela data da ocorrência (o dia em que a divergência foi aberta).
        </p>
      )}

      {mais && (
        <div className="dp-mais">
          {mostraStatus && (
            <label className="dp-rotulo">
              Status
              <select
                className="dp-select"
                style={{ width: '100%' }}
                value={f.status}
                onChange={(e) => mudar({ status: e.target.value as Status })}
              >
                {STATUS.map((s) => (
                  <option key={s.v} value={s.v}>
                    {s.r}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="dp-rotulo">
            Ordem / prioridade
            <select
              className="dp-select"
              style={{ width: '100%' }}
              value={f.ordem}
              onChange={(e) => mudar({ ordem: e.target.value as OrdemDaFila })}
            >
              {ORDENS.map((o) => (
                <option key={o.v} value={o.v}>
                  {o.r}
                </option>
              ))}
            </select>
          </label>
          <label className="dp-rotulo">
            Setor do Giro
            <select
              className="dp-select"
              style={{ width: '100%' }}
              value={f.setor}
              onChange={(e) => mudar({ setor: e.target.value })}
            >
              <option value="">Todos</option>
              {(dados?.setores ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="dp-rotulo">
            Diferença
            <select
              className="dp-select"
              style={{ width: '100%' }}
              value={f.faixa}
              onChange={(e) => mudar({ faixa: e.target.value as Faixa })}
            >
              <option value="">Qualquer</option>
              <option value="1">1 unidade</option>
              <option value="2-3">2 a 3</option>
              <option value="4-5">4 a 5</option>
              <option value="6-10">6 a 10</option>
              <option value="10+">Mais de 10</option>
            </select>
          </label>
          <label className="dp-check" style={{ alignSelf: 'end', minHeight: 38 }}>
            <input
              type="checkbox"
              checked={f.reincidentes}
              onChange={(e) => mudar({ reincidentes: e.target.checked })}
            />
            Só reincidentes
          </label>
        </div>
      )}
      {algumFiltro && (
        <div>
          <button
            type="button"
            className="dp-btn dp-btn-texto"
            onClick={() =>
              mudar({
                grupo: '',
                pessoa: '',
                setor: '',
                faixa: '',
                reincidentes: false,
                status: '',
              })
            }
          >
            Limpar filtros
          </button>
        </div>
      )}

      {estado.fase === 'erro' && !dados ? (
        <ErroDeArea
          oQue={
            estado.falha.tipo === 'offline' ? 'as divergências (sem conexão)' : 'as divergências'
          }
          aoTentar={() => void carregar(false)}
        />
      ) : !dados ? (
        <div className="dp-pagina" aria-busy="true">
          Carregando…
        </div>
      ) : dados.casos.length === 0 ? (
        <EstadoVazio
          icone={algumFiltro || f.busca ? 'search' : 'check'}
          titulo={
            algumFiltro || f.busca
              ? 'Nenhum caso com esses filtros'
              : local
                ? 'Nada esperando conferência aqui'
                : aba === 'ajustar'
                  ? 'Nada esperando decisão'
                  : 'Nenhum caso aqui'
          }
          texto={
            algumFiltro || f.busca
              ? 'Troque o período ou limpe os filtros para ver mais casos.'
              : local
                ? `Nenhum produto espera a conferência ${local === 'PRATELEIRA' ? 'da prateleira' : 'do estoque'}. Os novos aparecem sozinhos.`
                : 'Troque o período ou limpe os filtros para ver mais casos.'
          }
        />
      ) : (
        <>
          <ul className="dp-lista">
            {dados.casos.map((c) => (
              <CardDoCaso
                key={c.id}
                c={c}
                conferirEm={
                  conferindo && local && confereAqui
                    ? paraConferir(local, c.id)
                    : !papeis.decide && papeis.prateleira && c.faltaPrateleira
                      ? paraConferir('PRATELEIRA', c.id)
                      : !papeis.decide && papeis.estoque && c.faltaEstoque
                        ? paraConferir('ESTOQUE', c.id)
                        : null
                }
                analisarEm={papeis.decide || papeis.gestao ? paraCaso(c.id) : null}
                // Na fila de quem confere, "aguardando X" é óbvio: só o que pede atenção ganha selo.
                seloSoSeImporta={conferindo}
                verEm={paraCaso(c.id)}
              />
            ))}
          </ul>
          <div className="dp-pagina">
            <span className="dp-sync" data-estado={estado.fase === 'erro' ? 'erro' : 'ok'}>
              <Icone nome={estado.fase === 'erro' ? 'wifi' : 'cycle'} tamanho={13} />
              {estado.fase === 'erro'
                ? 'Sem conexão — mostrando a última leitura'
                : atualizadoEm
                  ? `Atualizado ${quandoBR(atualizadoEm)} · ${String(dados.total)} caso(s)`
                  : null}
            </span>
            {dados.total > POR_PAGINA && (
              <span className="dp-acoes">
                <button
                  type="button"
                  className="dp-btn dp-btn-p"
                  disabled={f.pagina <= 1}
                  onClick={() => setF((x) => ({ ...x, pagina: x.pagina - 1 }))}
                >
                  Anterior
                </button>
                <span style={{ alignSelf: 'center' }}>
                  {f.pagina} de {Math.ceil(dados.total / POR_PAGINA)}
                </span>
                <button
                  type="button"
                  className="dp-btn dp-btn-p"
                  disabled={f.pagina * POR_PAGINA >= dados.total}
                  onClick={() => setF((x) => ({ ...x, pagina: x.pagina + 1 }))}
                >
                  Próxima
                </button>
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function CardDoCaso({
  c,
  conferirEm,
  analisarEm,
  verEm,
  seloSoSeImporta,
}: {
  c: CasoDaFila;
  conferirEm: string | null;
  analisarEm: string | null;
  verEm: string;
  seloSoSeImporta: boolean;
}) {
  const s = seloDe(c);
  const prat = c.felipeEncontrou;
  // Estoque feito (14/10): contagem inteira, "mais de 12", ou a busca quando o HOS é acima de 6.
  const est: number | string | null =
    c.igorResultado === 'CONTOU'
      ? c.igorLocalizou
      : (c.igorResultado as string | null) === 'MAIS_DE'
        ? '+12'
        : c.modoEstoque === 'BUSCA' &&
            c.igorResultado !== null &&
            c.igorResultado !== 'NAO_VERIFICOU' &&
            c.igorLocalizou !== null
          ? `achou ${String(c.igorLocalizou)}/${String(c.alvo)}`
          : null;
  const nv = c.naoVerificado ?? null;
  return (
    <li className="dp-card" data-nivel={String(s.nivel)}>
      <FotoDoProduto url={c.fotoProduto} nome={c.produto} tamanho={56} />
      <div className="dp-card-corpo">
        <h2 className="dp-card-nome">{c.produto}</h2>
        <Laboratorio nome={c.fabricante} />
        <div className="dp-card-meta">
          {numeroDoCaso(c.numero)}
          {c.codigo ? ` · Código ${c.codigo}` : ''} · {c.setor} · B{c.bloco}
        </div>
        <div className="dp-card-linha">
          <span>
            Balconista: <strong>{c.autoria ?? '—'}</strong>
          </span>
          {!c.numeros && (
            <span>
              Diferença registrada: <span className="dp-dif">{comSinal(c.diferenca)}</span>
            </span>
          )}
          <span className="dp-card-meta">{quandoBR(c.abertoEm)}</span>
        </div>
        {c.numeros && <NumerosEmLinha n={c.numeros} />}
        {c.fluxo !== 'ANTERIOR' && c.estado !== 'CONCLUIDA' && (
          <div className="dp-progresso" aria-label="Contagens">
            <span
              className="dp-passo"
              data-feito={prat !== null}
              data-falhou={nv?.local === 'PRATELEIRA'}
            >
              <Icone
                nome={prat !== null ? 'check' : nv?.local === 'PRATELEIRA' ? 'warn' : 'clock'}
                tamanho={11}
                traco={2}
              />
              Prateleira
              {prat !== null
                ? ` ${String(prat)}`
                : nv?.local === 'PRATELEIRA'
                  ? ' não verificada'
                  : ''}
            </span>
            <span
              className="dp-passo"
              data-feito={est !== null}
              data-falhou={nv?.local === 'ESTOQUE'}
            >
              <Icone
                nome={est !== null ? 'check' : nv?.local === 'ESTOQUE' ? 'warn' : 'clock'}
                tamanho={11}
                traco={2}
              />
              Estoque
              {est !== null ? ` ${String(est)}` : nv?.local === 'ESTOQUE' ? ' não verificado' : ''}
            </span>
          </div>
        )}
      </div>
      <div className="dp-card-acao">
        {(!seloSoSeImporta || s.nivel <= 1) && <Selo selo={s.selo} titulo={s.motivo} />}
        {conferirEm ? (
          <a className="dp-btn dp-btn-primario" href={conferirEm}>
            Conferir produto
            <Icone nome="arrow" tamanho={16} traco={2} />
          </a>
        ) : analisarEm ? (
          <a
            className={`dp-btn ${c.estado === 'CONCLUIDA' ? '' : 'dp-btn-escuro'}`}
            href={analisarEm}
          >
            {c.estado === 'CONCLUIDA' ? 'Ver caso' : 'Analisar caso'}
            <Icone nome="arrow" tamanho={16} traco={2} />
          </a>
        ) : (
          <a className="dp-btn" href={verEm}>
            Ver caso
          </a>
        )}
      </div>
    </li>
  );
}
